import axios from "axios";
import { useRouter } from "next/router";
import { useEffect, useState } from "react";

import { HTTP_STATUS } from "../../api/wv/consts";
import useUserAuthStore, {
  checkLoggedIn,
} from "../../store/client/user-management/auth";
import { USER_MANAGEMENT_PAGE_PATH } from "../../store/client/user-management/consts";
import { ErrorMessage } from "../../utils/common/getErrorMessage";
import { LOCALES_SHORT } from "../../utils/types";

// 로그인한 유저에 대한 예외 처리, 로그아웃 처리
// redirectIfAuthenticated는 로그인한 유저가 해당 페이지에 접근할 때 접근을 막고 리다이렉트하고 싶을 때 사용

const useAuth = (redirect = true) => {
  const router = useRouter();

  const {
    initializeAuthState,
    currentUser,
    token,
    googleSignIn,
    appleSignIn,
    phoneSignIn,
    emailSignIn,
    sendResetPasswordEmail,
    loginMethod,
    phoneNumber,
    email,
    logOut,
    isLoading,
  } = useUserAuthStore();
  const { locale } = useRouter();
  const transformLocale =
    locale === LOCALES_SHORT.KO ? LOCALES_SHORT.KO : LOCALES_SHORT.EN;
  const isLoggedIn = checkLoggedIn();
  const [isInitialLoading, setIsInitialLoading] = useState<boolean>(true);
  const [error, setError] = useState<ErrorMessage | null>(null);

  useEffect(() => {
    initializeAuthState(isInitialLoading, setIsInitialLoading);
  }, [initializeAuthState, isInitialLoading]);

  useEffect(() => {
    // 헤더에서 사용하는 경우는 아무것도 하지 않음
    if (!redirect) {
      return;
    }

    if (currentUser === undefined) {
      return; // 초기 상태에서는 아무것도 하지 않음
    }

    // 로그인 상태가 아닐 때,
    // 로그인이 필요한 페이지로 접근 시 로그인 페이지로 리다이렉트
    if (!currentUser && !redirect) {
      router.push(USER_MANAGEMENT_PAGE_PATH.LOGIN_IN);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- 의도: 인증 상태 변화 시에만 리다이렉트 판단. router는 래퍼 특성상 identity 불안정 → 추가 시 effect 재실행 반복 전례(fetch 루프) 있음
  }, [currentUser, redirect, isLoggedIn]);

  useEffect(() => {
    const interceptor = axios.interceptors.response.use(
      response => response,
      async err => {
        const { status } = err.response || {};

        if (status === HTTP_STATUS.UNAUTHORIZED) {
          const response = await logOut(transformLocale);
          if (response && !response.success) {
            console.error("Log out error: ", response.error);
          }
        }

        return Promise.reject(err);
      }
    );

    return () => {
      axios.interceptors.response.eject(interceptor);
    };
  }, [logOut, transformLocale]);

  return {
    currentUser,
    token,
    googleSignIn,
    appleSignIn,
    phoneSignIn,
    emailSignIn,
    sendResetPasswordEmail,
    loginMethod,
    phoneNumber,
    email,
    logOut,
    error,
    setError,
    isLoading,
    isInitialLoading,
  };
};

export default useAuth;
